// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Center, Group, Loader, Text } from '@mantine/core';
import type { BundleEntry, Communication } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { TriageSortState } from '@polarissleep/shared';
import type { DataTableSortStatus } from 'mantine-datatable';
import { DataTable } from 'mantine-datatable';
import type { JSX } from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { TableAnchor } from '../../components/layout/TableAnchor';
import { type IneligibleEntry, useIneligibleQueue } from '../../hooks/useIneligibleQueue';
import { sortStatusToTriageSortState, triageSortStateToSortStatus } from './useTriageQueueUrlState';

interface IneligibleTableProps {
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  sortState: TriageSortState | null;
  onSortChange: (next: TriageSortState | null) => void;
  search?: string;
  hideTestPatients: boolean;
  onTotalChange?: (total: number) => void;
}

function formatDate(dateString: string | undefined): string {
  if (!dateString) {
    return '-';
  }
  return new Date(dateString).toLocaleDateString();
}

export function IneligibleTable({
  page,
  pageSize,
  onPageChange,
  sortState,
  onSortChange,
  search,
  hideTestPatients,
  onTotalChange,
}: IneligibleTableProps): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const { entries, loading, total } = useIneligibleQueue({
    page,
    pageSize,
    sort: sortState ?? undefined,
    search,
    hideTestPatients,
  });

  useEffect(() => onTotalChange?.(total), [onTotalChange, total]);

  if (loading && entries.length === 0) {
    return (
      <Center h={200}>
        <Loader size="md" />
      </Center>
    );
  }

  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  const columns = [
    {
      // Patient name sort uses the native Patient family SearchParameter (_sort=family).
      // Handled by the default branch in useIneligibleQueue.
      accessor: 'patientName' as const,
      title: 'Patient',
      sortable: true,
      render: (entry: IneligibleEntry) => (
        <TableAnchor
          size="sm"
          onClick={async () => {
            await navigate(`/Patient/${entry.patientId}`);
          }}
          style={{ cursor: 'pointer' }}
        >
          {entry.patientName}
        </TableAnchor>
      ),
    },
    {
      accessor: 'intakeCompletedDate' as const,
      title: 'Date Checked',
      sortable: true,
      render: (entry: IneligibleEntry) => <Text size="sm">{formatDate(entry.intakeCompletedDate)}</Text>,
    },
    {
      accessor: 'status' as const,
      title: 'Status',
      sortable: false,
      render: () => <Text size="sm">Ineligible</Text>,
    },
    {
      accessor: 'eligibility' as const,
      title: 'Eligibility',
      sortable: false,
      render: (_entry: IneligibleEntry) => <Text size="sm">Ineligible</Text>,
    },
    {
      accessor: 'errorCode' as const,
      title: 'Error Code',
      sortable: false,
      render: (entry: IneligibleEntry) => <Text size="sm">{entry.errorCode ?? '-'}</Text>,
    },
    {
      accessor: 'requestedStc' as const,
      title: 'Requested STC',
      sortable: false,
      render: (entry: IneligibleEntry) => (
        <Text size="sm">
          {entry.requestedStc ? entry.requestedStc.charAt(0).toUpperCase() + entry.requestedStc.slice(1) : '-'}
        </Text>
      ),
    },
    {
      accessor: 'payorId' as const,
      title: 'Payor ID',
      sortable: false,
      render: (entry: IneligibleEntry) => <Text size="sm">{entry.payorId ?? '-'}</Text>,
    },
    {
      accessor: 'actions' as const,
      title: 'Actions',
      sortable: false,
      render: (entry: IneligibleEntry) => (
        <Group gap="xs" wrap="nowrap">
          <TableAnchor
            size="sm"
            onClick={async () => {
              if (entry.systemStatusCode === 'prospect' && entry.compositionId) {
                await navigate(`/triage/composition/${entry.compositionId}`);
              } else {
                await navigate(`/Patient/${entry.patientId}`);
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            {entry.systemStatusCode === 'prospect' ? 'View Screener' : 'View Chart'}
          </TableAnchor>
          {entry.systemStatusCode !== 'prospect' && (
            <TableAnchor
              size="sm"
              onClick={async () => {
                const bundle = await medplum.search('Communication', {
                  subject: `Patient/${entry.patientId}`,
                  status: 'in-progress',
                  _sort: '-_lastUpdated',
                  _count: '10',
                });
                const comm = (bundle.entry ?? [])
                  .map((e: BundleEntry) => e.resource as Communication)
                  .find((c: Communication) => c.topic && (c.topic.text || c.topic.coding?.length));
                if (comm?.id) {
                  await navigate(`/Patient/${entry.patientId}/Communication/${comm.id}`);
                } else {
                  await navigate(`/Patient/${entry.patientId}/Communication`);
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              Message
            </TableAnchor>
          )}
        </Group>
      ),
    },
  ];

  // Bridge between mantine-datatable's columnAccessor (which uses the column `accessor`
  // field) and the TriageSortColumnKey URL contract:
  //   - 'patientName' accessor ↔ 'name' sort key (Patient family SP)
  //   - Other accessor names: currently no shared Observation columns are rendered
  //     by IneligibleTable — those columns are on TriagePage, not IneligibleTable.
  //   - sortStatusToTriageSortState handles the 'name' mapping: the columnAccessor
  //     must equal the TriageSortColumnKey, so we translate 'patientName' ↔ 'name'
  //     at the boundary.
  const sortStatus: DataTableSortStatus<IneligibleEntry> = (() => {
    const status = triageSortStateToSortStatus(sortState);
    if (!status) {
      return { columnAccessor: '', direction: 'desc' as const };
    }
    const accessorMap: Record<string, string> = {
      name: 'patientName',
      intakeDate: 'intakeCompletedDate',
      systemStatus: 'systemStatusCode',
    };
    const columnAccessor = accessorMap[status.columnAccessor] ?? status.columnAccessor;
    return { columnAccessor, direction: status.direction };
  })();

  return (
    <>
      {total > 0 && (
        <Text size="sm" c="dimmed">
          {start} - {end} of {total}
        </Text>
      )}
      <DataTable
        striped
        highlightOnHover
        fetching={loading && entries.length === 0}
        records={entries}
        columns={columns}
        idAccessor="patientId"
        noRecordsText="No ineligible patients found"
        page={page}
        recordsPerPage={pageSize}
        totalRecords={total}
        onPageChange={onPageChange}
        paginationSize="sm"
        minHeight={200}
        sortStatus={sortStatus}
        onSortStatusChange={(status: DataTableSortStatus<IneligibleEntry>) => {
          const keyMap: Record<string, string> = {
            patientName: 'name',
            intakeCompletedDate: 'intakeDate',
            systemStatusCode: 'systemStatus',
          };
          const sortKey = keyMap[status.columnAccessor];
          if (!sortKey) {
            onSortChange(null);
            return;
          }
          const next = sortStatusToTriageSortState({ columnAccessor: sortKey, direction: status.direction });
          if (
            next &&
            sortState &&
            sortState.column === next.column &&
            sortState.direction === 'asc' &&
            next.direction === 'desc'
          ) {
            onSortChange(null);
          } else {
            onSortChange(next);
          }
        }}
      />
    </>
  );
}
